import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, from, Observable, of, switchMap, tap, throwError } from 'rxjs';
import {
  Desk,
  GroupCreateRequest,
  DeskUpdateRequest,
  Floor,
  FloorCreateRequest,
  FloorEditRequest,
  Group,
  Label,
  LabelUpdateRequest,
  LabelCreateRequest,
} from '../models';
import { AlertService, AlertType } from './alert.service';
import { PerformanceMonitorService } from './performance-monitor.service';

@Injectable({
  providedIn: 'root',
})
export class OfficeManagementService {
  // injects
  private readonly alertService = inject(AlertService);
  private readonly httpClient = inject(HttpClient);
  private readonly translate = inject(TranslateService);
  private readonly performanceMonitor = inject(PerformanceMonitorService);

  // Cache configuration
  private readonly FLOOR_IMAGE_CACHE_KEY = 'dms.floorImages';
  private readonly LOCAL_STORAGE_CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours
  private readonly MEMORY_CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes
  private readonly MAX_CACHE_SIZE = 10 * 1024 * 1024; // 10MB
  private readonly MAX_IMAGE_SIZE = 1024 * 1024; // 1MB for compression threshold

  // api urls
  private readonly urlBase: string = 'api/office-management';
  private readonly urlDesks: string = `${this.urlBase}/desk`;
  private readonly urlFloors: string = `${this.urlBase}/floor`;
  private readonly urlLabels: string = `${this.urlBase}/label`;

  // Memory cache for floor images
  private readonly floorImageCache = new Map<
    number,
    { blob: Blob; url: string; timestamp: number }
  >();

  // signals
  groups = signal<Group[]>([]);
  desks = signal<Desk[]>([]);
  floors = signal<Floor[]>([]);
  labels = signal<Label[]>([]);

  constructor() {
    // Clean up expired items from localStorage on service init
    this.cleanupExpiredCache();
  }

  createGroup(payload: GroupCreateRequest): Observable<Group> {
    this.performanceMonitor.startMeasure('createGroup');
    return this.httpClient.post<Group>(`${this.urlDesks}/create`, payload).pipe(
      tap((group) => {
        // After a successful group creation, invalidate the cache for this floor
        this.floorGroupsCache.delete(payload.floorId);
        this.onGroupCreated(group);
        this.performanceMonitor.endMeasure('createGroup');
      }),
      catchError((error) => {
        this.performanceMonitor.endMeasure('createGroup');
        return this.handleError(error);
      }),
    );
  }

  private onGroupCreated(group: Group) {
    this.performanceMonitor.startMeasure('groupCreatedEvent');
    const event = new CustomEvent('group-created', { detail: group });
    window.dispatchEvent(event);
    this.performanceMonitor.endMeasure('groupCreatedEvent');
  }

  createFloor({ floorName, image }: FloorCreateRequest): Observable<Floor> {
    this.performanceMonitor.startMeasure('createFloor');

    return from(this.compressImage(image)).pipe(
      switchMap((compressedImage) => {
        const formData = new FormData();
        formData.append('floorName', floorName);
        formData.append('image', compressedImage);

        return this.httpClient.post<Floor>(`${this.urlFloors}/create`, formData).pipe(
          tap(({ name }) => {
            this.handleSuccess(
              `${this.translate.instant('ADMIN.OFFICE_SPACE.CREATE_FLOOR_SUCCESS')} '${name}'`,
            );
            this.performanceMonitor.endMeasure('createFloor');
          }),
          catchError((error) => {
            this.performanceMonitor.endMeasure('createFloor');
            return this.handleError(error);
          }),
        );
      }),
    );
  }

  getAllFloors() {
    this.performanceMonitor.startMeasure('getAllFloors');
    this.httpClient
      .get<Floor[]>(`${this.urlFloors}/all`)
      .pipe(
        tap(() => this.performanceMonitor.endMeasure('getAllFloors')),
        catchError((error) => {
          this.performanceMonitor.endMeasure('getAllFloors');
          return this.handleError(error);
        }),
      )
      .subscribe((response) => this.floors.set(response));
  }

  getFloorImage(floorId: number): Observable<Blob> {
    this.performanceMonitor.startMeasure('getFloorImage');

    // Check memory cache first
    const memCached = this.floorImageCache.get(floorId);
    if (memCached && Date.now() - memCached.timestamp < this.MEMORY_CACHE_EXPIRY) {
      this.performanceMonitor.endMeasure('getFloorImage');
      return of(memCached.blob);
    }

    // Then check localStorage cache
    const localCached = this.getCachedImage(floorId);
    if (localCached) {
      // Update memory cache
      this.floorImageCache.set(floorId, {
        blob: localCached.blob,
        url: URL.createObjectURL(localCached.blob),
        timestamp: Date.now(),
      });
      this.performanceMonitor.endMeasure('getFloorImage');
      return of(localCached.blob);
    }

    // If not in cache, fetch from server
    return this.httpClient
      .get(`${this.urlFloors}/${floorId}/image`, {
        responseType: 'blob',
      })
      .pipe(
        tap((blob) => {
          // Update both caches
          const url = URL.createObjectURL(blob);
          this.floorImageCache.set(floorId, {
            blob: blob.slice(),
            url,
            timestamp: Date.now(),
          });
          this.cacheImage(floorId, blob.slice());
        }),
        tap(() => this.performanceMonitor.endMeasure('getFloorImage')),
        catchError((error) => {
          this.performanceMonitor.endMeasure('getFloorImage');
          return this.handleError(error);
        }),
      );
  }

  private floorGroupsCache = new Map<number, { groups: Group[]; timestamp: number }>();
  private readonly FLOOR_GROUPS_CACHE_EXPIRY = 1 * 30 * 1000; // 5 minutes

  getFloorGroups(floorId: number) {
    this.performanceMonitor.startMeasure('getFloorGroups');

    // Check cache first
    const cached = this.floorGroupsCache.get(floorId);
    if (cached && Date.now() - cached.timestamp < this.FLOOR_GROUPS_CACHE_EXPIRY) {
      this.groups.set(cached.groups);
      this.performanceMonitor.endMeasure('getFloorGroups');
      return;
    }

    this.httpClient
      .get<Group[]>(`${this.urlDesks}/list/floor/${floorId}`)
      .pipe(
        tap((groups) => {
          // Cache the response
          this.floorGroupsCache.set(floorId, {
            groups,
            timestamp: Date.now(),
          });
          this.groups.set(groups);
        }),
        tap(() => this.performanceMonitor.endMeasure('getFloorGroups')),
        catchError((error) => {
          this.performanceMonitor.endMeasure('getFloorGroups');
          return this.handleError(error);
        }),
      )
      .subscribe();
  }

  // Private helper methods
  private async compressImage(file: File | Blob): Promise<Blob> {
    this.performanceMonitor.startMeasure('compressImage');

    if (file.size <= this.MAX_IMAGE_SIZE) {
      this.performanceMonitor.endMeasure('compressImage');
      return file;
    }

    return new Promise((resolve) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);

      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions while maintaining aspect ratio
        const aspectRatio = width / height;
        if (width > 2048) {
          width = 2048;
          height = Math.round(width / aspectRatio);
        }
        if (height > 2048) {
          height = 2048;
          width = Math.round(height * aspectRatio);
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(img.src);
            this.performanceMonitor.endMeasure('compressImage');
            resolve(blob);
          },
          'image/jpeg',
          0.8,
        );
      };
    });
  }

  private getCachedImage(floorId: number): { blob: Blob; timestamp: number } | null {
    try {
      const cache = localStorage.getItem(`${this.FLOOR_IMAGE_CACHE_KEY}.${floorId}`);
      if (!cache) return null;

      const { blob: base64Data, timestamp } = JSON.parse(cache);
      if (Date.now() - timestamp > this.LOCAL_STORAGE_CACHE_EXPIRY) {
        localStorage.removeItem(`${this.FLOOR_IMAGE_CACHE_KEY}.${floorId}`);
        return null;
      }

      // Convert base64 back to Blob
      const byteString = atob(base64Data);
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const uint8Array = new Uint8Array(arrayBuffer);

      for (let i = 0; i < byteString.length; i++) {
        uint8Array[i] = byteString.charCodeAt(i);
      }

      const blob = new Blob([uint8Array], { type: 'image/png' });
      return { blob, timestamp };
    } catch (error) {
      console.error('Error reading from cache:', error);
      localStorage.removeItem(`${this.FLOOR_IMAGE_CACHE_KEY}.${floorId}`);
      return null;
    }
  }

  private async cacheImage(floorId: number, blob: Blob): Promise<void> {
    try {
      // Convert Blob to base64
      const base64Data = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result as string;
          resolve(base64.split(',')[1]); // Remove data URL prefix
        };
        reader.readAsDataURL(blob);
      });

      // Check cache size before storing
      let totalSize = 0;
      const keys = Object.keys(localStorage);
      for (const key of keys) {
        if (key.startsWith(this.FLOOR_IMAGE_CACHE_KEY)) {
          totalSize += localStorage.getItem(key).length;
        }
      }

      // If cache is too large, remove oldest entries
      if (totalSize > this.MAX_CACHE_SIZE) {
        const cacheKeys = keys
          .filter((k) => k.startsWith(this.FLOOR_IMAGE_CACHE_KEY))
          .sort((a, b) => {
            const timeA = JSON.parse(localStorage.getItem(a)).timestamp;
            const timeB = JSON.parse(localStorage.getItem(b)).timestamp;
            return timeA - timeB;
          });

        while (totalSize > this.MAX_CACHE_SIZE && cacheKeys.length > 0) {
          const oldestKey = cacheKeys.shift();
          totalSize -= localStorage.getItem(oldestKey).length;
          localStorage.removeItem(oldestKey);
        }
      }

      // Store new image
      localStorage.setItem(
        `${this.FLOOR_IMAGE_CACHE_KEY}.${floorId}`,
        JSON.stringify({
          blob: base64Data,
          timestamp: Date.now(),
        }),
      );
    } catch (error) {
      console.error('Error caching image:', error);
    }
  }

  private cleanupExpiredCache(): void {
    const now = Date.now();
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (key.startsWith(this.FLOOR_IMAGE_CACHE_KEY)) {
        try {
          const { timestamp } = JSON.parse(localStorage.getItem(key));
          if (now - timestamp > this.LOCAL_STORAGE_CACHE_EXPIRY) {
            localStorage.removeItem(key);
          }
        } catch (error) {
          console.error('Error cleaning up cache:', error);
          localStorage.removeItem(key);
        }
      }
    }
  }

  removeGroup(id: number, floorId: number): Observable<any> {
    this.performanceMonitor.startMeasure('removeGroup');
    return this.httpClient.delete<void>(`${this.urlDesks}/${id}`).pipe(
      tap(() => {
        // Find which floor this group belonged to by checking all cached floors
        this.floorGroupsCache.delete(floorId);

        this.handleSuccess(`${this.translate.instant('ADMIN.OFFICE_SPACE.REMOVE_DESK_SUCCESS')}`);
      }),
      tap(() => this.performanceMonitor.endMeasure('removeGroup')),
      catchError((error) => {
        this.performanceMonitor.endMeasure('removeGroup');
        return this.handleError(error);
      }),
    );
  }

  removeFloor(floorId: number): Observable<any> {
    return this.httpClient.delete<Floor>(`${this.urlFloors}/${floorId}`).pipe(
      tap(() =>
        this.handleSuccess(`${this.translate.instant('ADMIN.OFFICE_SPACE.REMOVE_FLOOR_SUCCESS')}`),
      ),
      catchError((error) => {
        return this.handleError(error);
      }),
    );
  }

  updateGroup(payload: DeskUpdateRequest, floorId: number): Observable<Group> {
    this.performanceMonitor.startMeasure('updateGroup');
    return this.httpClient.put<Group>(`${this.urlDesks}/update`, [payload]).pipe(
      tap(() => {
        // Invalidate cache for this floor
        this.floorGroupsCache.delete(floorId);
        this.performanceMonitor.endMeasure('updateGroup');
      }),
      catchError((error) => {
        this.performanceMonitor.endMeasure('updateGroup');
        return this.handleError(error);
      }),
    );
  }

  updateFloor({ isActive, id, image, name, isPrimary }: FloorEditRequest): Observable<Floor> {
    this.performanceMonitor.startMeasure('updateFloor');
    const formData = new FormData();

    formData.append('active', String(isActive));
    formData.append('id', String(id));
    formData.append('name', name);
    formData.append('primary', String(isPrimary));

    if (image) {
      formData.append('image', image);
    }

    return this.httpClient.put<Floor>(`${this.urlFloors}/update`, formData).pipe(
      tap(({ name }) =>
        this.handleSuccess(
          `${this.translate.instant('ADMIN.OFFICE_SPACE.UPDATE_FLOOR_SUCCESS')} '${name}'`,
        ),
      ),
      tap(() => this.getAllFloors()),
      catchError((error) => {
        this.performanceMonitor.endMeasure('updateFloor');
        return this.handleError(error);
      }),
    );
  }

  createLabel(request: LabelCreateRequest): Observable<Label> {
    this.performanceMonitor.startMeasure('createLabel');
    return this.httpClient.post<Label>(`${this.urlLabels}/create`, request).pipe(
      tap(() => this.performanceMonitor.endMeasure('createLabel')),
      catchError((error) => {
        this.performanceMonitor.endMeasure('createLabel');
        return this.handleError(error);
      }),
    );
  }

  updateLabel(request: LabelUpdateRequest): Observable<Label> {
    this.performanceMonitor.startMeasure('updateLabel');
    return this.httpClient.put<Label>(`${this.urlLabels}/update`, request).pipe(
      tap(() => this.performanceMonitor.endMeasure('updateLabel')),
      catchError((error) => {
        this.performanceMonitor.endMeasure('updateLabel');
        return this.handleError(error);
      }),
    );
  }

  deleteLabel(id: number): Observable<void> {
    this.performanceMonitor.startMeasure('deleteLabel');
    return this.httpClient.delete<void>(`${this.urlLabels}/${id}`).pipe(
      tap(() => this.performanceMonitor.endMeasure('deleteLabel')),
      catchError((error) => {
        this.performanceMonitor.endMeasure('deleteLabel');
        return this.handleError(error);
      }),
    );
  }

  getAllLabelsByFloor(floorId: number) {
    this.performanceMonitor.startMeasure('getAllLabelsByFloor');
    this.httpClient
      .get<Label[]>(`${this.urlLabels}/list/floor/${floorId}`)
      .pipe(
        tap(() => this.performanceMonitor.endMeasure('getAllLabelsByFloor')),
        catchError((error) => {
          this.performanceMonitor.endMeasure('getAllLabelsByFloor');
          return this.handleError(error);
        }),
      )
      .subscribe((response) => this.labels.set(response));
  }

  public clearFloorImageCache(floorId: number): void {
    this.floorImageCache.delete(floorId);
    localStorage.removeItem(`${this.FLOOR_IMAGE_CACHE_KEY}.${floorId}`);
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    const message =
      typeof error.error === 'string' && error.error
        ? error.error
        : 'SHARED.ERRORS.SOMETHING_WENT_WRONG';
    this.alertService.showAlert(message, AlertType.error);
    return throwError(() => error);
  }

  private handleSuccess(message: string) {
    this.alertService.showAlert(message, AlertType.success);
  }
}
